/**
 * Terrain Preview — generates thumbnail display objects for stage select cells.
 *
 * Named stages use vector wireframe rendering (crisp PixiJS Graphics, ~0.1ms each).
 * Random uses procedural terrain + neon silhouette pixel buffer (~75ms).
 * Previews are generated one per frame to avoid stuttering.
 */

import { Container, Graphics, Sprite, Texture } from 'pixi.js';
import { TERRAIN, CELL } from '../../constants';
import { generateTerrain } from '../../terrain/generation';
import { getGrid, createGrid, importGridBase64, rebuildColumnTopCache } from '../../terrain/grid';
import { getAllStageIds, getStage } from '../../stages';
import type { StageTheme } from '../../stages';
import { getBakedPreset } from '../../stages/presets';

// GRID_WIDTH accessed via TERRAIN.GRID_WIDTH (dynamic for wide stages)

// Neon glow parameters
const GLOW_RADIUS = 3;      // thumbnail pixels
const GLOW_STRENGTH = 0.45; // max blend alpha at distance 1

// Thumbnail dimensions
const THUMB_W = 196;
const THUMB_H = 98;

// Vector preview layout
const PAD = 8;
const CONTENT_W = THUMB_W - 2 * PAD;  // 180
const CONTENT_H = THUMB_H - 2 * PAD;  // 82
const CORNER_RADIUS = 6;
const GROUND_VISUAL_DEPTH = 120;       // world units of visual mass below surfaceY

// ── State ──────────────────────────────────────────────────

interface PreviewEntry {
  display: Container;
  texture: Texture | null;
}

const previews = new Map<string, PreviewEntry>();
let generationQueue: string[] = [];
let randomSeed = 0;
let randomThemeId = 'neon_void';

// ── Public API ─────────────────────────────────────────────

/** Queue all stages for preview generation. Call once when stage select opens. */
export function startPreviewGeneration(seed: number, themeId: string): void {
  clearPreviews();
  randomSeed = seed;
  randomThemeId = themeId;
  generationQueue = getAllStageIds().filter(id => id !== 'random');
  generationQueue.push('random');
}

/** Generate one preview per call. Returns true if still working. */
export function tickPreviewGeneration(): boolean {
  if (generationQueue.length === 0) return false;
  const stageId = generationQueue.shift()!;
  if (stageId === 'random') {
    generatePixelPreview();
  } else if (getBakedPreset(stageId)) {
    generateBakedPixelPreview(stageId);
  } else {
    generateVectorPreview(stageId);
  }
  return generationQueue.length > 0;
}

/** Get the preview for a stage (null if not yet generated). */
export function getPreviewSprite(stageId: string): Container | null {
  return previews.get(stageId)?.display ?? null;
}

/** Regenerate the random preview with a new seed + theme. */
export function rerollRandomPreview(seed: number, themeId: string): void {
  randomSeed = seed;
  randomThemeId = themeId;
  const old = previews.get('random');
  if (old) {
    old.display.destroy({ children: true });
    if (old.texture) old.texture.destroy();
    previews.delete('random');
  }
  generatePixelPreview();
}

/** Free all preview resources. */
export function clearPreviews(): void {
  for (const entry of previews.values()) {
    entry.display.destroy({ children: true });
    if (entry.texture) entry.texture.destroy();
  }
  previews.clear();
  generationQueue = [];
}

// ── Vector Preview (Named Stages) ──────────────────────────

function generateVectorPreview(stageId: string): void {
  const stage = getStage(stageId);
  const theme = stage.theme;
  const layout = stage.generateLayout(280, 2000, 2);

  // ── Bounding box ──
  let minX = Infinity, maxX = -Infinity;
  let minY = Infinity, maxY = -Infinity;

  for (const g of layout.groundSegments) {
    minX = Math.min(minX, g.x);
    maxX = Math.max(maxX, g.x + g.width);
    minY = Math.min(minY, g.surfaceY);
    maxY = Math.max(maxY, g.surfaceY + GROUND_VISUAL_DEPTH);
  }

  for (const p of layout.platforms) {
    minX = Math.min(minX, p.x);
    maxX = Math.max(maxX, p.x + p.width);
    minY = Math.min(minY, p.y);
    maxY = Math.max(maxY, p.y + Math.max(p.thickness * 2, 4));
  }

  for (const n of layout.nodes) {
    const r = n.radius * 2;
    minX = Math.min(minX, n.x - r);
    maxX = Math.max(maxX, n.x + r);
    minY = Math.min(minY, n.y - r);
    maxY = Math.max(maxY, n.y + r);
  }

  // Fallback for empty layouts
  if (!isFinite(minX)) { minX = 280; maxX = 2280; minY = 400; maxY = 800; }

  // ── Scale to fit ──
  const rangeX = maxX - minX || 1;
  const rangeY = maxY - minY || 1;
  const scale = Math.min(CONTENT_W / rangeX, CONTENT_H / rangeY);
  const offsetX = PAD + (CONTENT_W - rangeX * scale) / 2;
  const offsetY = PAD + (CONTENT_H - rangeY * scale) / 2;

  const mapX = (wx: number) => (wx - minX) * scale + offsetX;
  const mapY = (wy: number) => (wy - minY) * scale + offsetY;

  // ── Theme colors ──
  const [sr, sg, sb] = theme.sandSurface;
  const terrainFill = (sr << 16) | (sg << 8) | sb;
  const edgeColor = theme.sandEdgeColor;
  const wireEdge = theme.wireEdgeColor;
  const voidEdge = theme.voidEdgeColor;

  const bottomY = mapY(maxY);

  const gfx = new Graphics();

  // Background
  gfx.roundRect(0, 0, THUMB_W, THUMB_H, CORNER_RADIUS);
  gfx.fill({ color: theme.bgColor });

  // Ground segments — solid mass from surfaceY down to bottom
  for (const g of layout.groundSegments) {
    const x0 = mapX(g.x);
    const x1 = mapX(g.x + g.width);
    const y0 = mapY(g.surfaceY);
    const w = x1 - x0;
    const h = bottomY - y0;
    if (w > 0 && h > 0) {
      gfx.rect(x0, y0, w, h);
      gfx.fill({ color: terrainFill });
      gfx.moveTo(x0, y0).lineTo(x1, y0);
      gfx.stroke({ color: edgeColor, width: 2 });
    }
  }

  // Platforms — thin bars with edge glow
  for (const p of layout.platforms) {
    const x0 = mapX(p.x);
    const x1 = mapX(p.x + p.width);
    const y0 = mapY(p.y);
    const w = x1 - x0;
    const h = Math.max(3, p.thickness * scale);
    if (w > 0) {
      gfx.rect(x0, y0, w, h);
      gfx.fill({ color: terrainFill });
      gfx.moveTo(x0, y0).lineTo(x0 + w, y0);
      gfx.stroke({ color: edgeColor, width: 1.5 });
    }
  }

  // Beams — lines between nodes
  for (const b of layout.beams) {
    const from = layout.nodes[b.from];
    const to = layout.nodes[b.to];
    if (!from || !to) continue;
    gfx.moveTo(mapX(from.x), mapY(from.y)).lineTo(mapX(to.x), mapY(to.y));
    gfx.stroke({ color: wireEdge, width: Math.max(1.5, b.thickness * scale) });
  }

  // Nodes — circles
  for (const n of layout.nodes) {
    gfx.circle(mapX(n.x), mapY(n.y), Math.max(2, n.radius * scale));
    gfx.fill({ color: wireEdge });
  }

  // Void hint — semi-transparent gradient strip along bottom 15%
  const voidTop = THUMB_H * 0.85;
  const voidH = THUMB_H - voidTop;
  for (let i = 0; i < 4; i++) {
    const t = i / 4;
    gfx.rect(0, voidTop + t * voidH, THUMB_W, voidH / 4 + 1);
    gfx.fill({ color: voidEdge, alpha: 0.05 + t * 0.12 });
  }

  previews.set(stageId, { display: gfx, texture: null });
}

// ── Pixel Preview (Baked Stages) ───────────────────────────

function generateBakedPixelPreview(stageId: string): void {
  const baked = getBakedPreset(stageId);
  if (!baked) return;
  const theme = getStage(stageId).theme;

  // Load baked grid into terrain system
  createGrid();
  importGridBase64(baked.gridData);
  rebuildColumnTopCache();

  const grid = getGrid();
  if (!grid) return;

  const gridHeight = grid.length / TERRAIN.GRID_WIDTH;
  const pixels = renderNeonPixels(grid, gridHeight, theme);

  const texture = Texture.from({ resource: pixels, width: THUMB_W, height: THUMB_H });
  const sprite = new Sprite(texture);

  const mask = new Graphics();
  mask.roundRect(0, 0, THUMB_W, THUMB_H, CORNER_RADIUS);
  mask.fill({ color: 0xffffff });

  const wrapper = new Container();
  wrapper.addChild(sprite);
  wrapper.addChild(mask);
  sprite.mask = mask;

  previews.set(stageId, { display: wrapper, texture });
}

// ── Pixel Preview (Random) ─────────────────────────────────

function generatePixelPreview(): void {
  generateTerrain(2, randomSeed);
  const theme = getStage(randomThemeId).theme;
  const grid = getGrid();
  if (!grid) return;

  const gridHeight = grid.length / TERRAIN.GRID_WIDTH;
  const pixels = renderNeonPixels(grid, gridHeight, theme);

  const texture = Texture.from({ resource: pixels, width: THUMB_W, height: THUMB_H });
  const sprite = new Sprite(texture);

  // Rounded-corner mask to match vector previews
  const mask = new Graphics();
  mask.roundRect(0, 0, THUMB_W, THUMB_H, CORNER_RADIUS);
  mask.fill({ color: 0xffffff });

  const wrapper = new Container();
  wrapper.addChild(sprite);
  wrapper.addChild(mask);
  sprite.mask = mask;

  previews.set('random', { display: wrapper, texture });
}

// ── Helpers ────────────────────────────────────────────────

function hasEmptyNeighbor(grid: Uint8Array, gx: number, gy: number, w: number, h: number): boolean {
  if (gx > 0 && grid[gy * w + gx - 1] === 0) return true;
  if (gx < w - 1 && grid[gy * w + gx + 1] === 0) return true;
  if (gy > 0 && grid[(gy - 1) * w + gx] === 0) return true;
  if (gy < h - 1 && grid[(gy + 1) * w + gx] === 0) return true;
  return false;
}

/**
 * Neon silhouette renderer — flat dark terrain fill with bright edge outlines
 * and soft outer glow. Produces clean, readable shapes at thumbnail scale.
 */
function renderNeonPixels(grid: Uint8Array, gridHeight: number, theme: StageTheme): Uint8Array {
  const pixels = new Uint8Array(THUMB_W * THUMB_H * 4);

  const bgR = (theme.bgColor >> 16) & 0xff;
  const bgG = (theme.bgColor >> 8) & 0xff;
  const bgB = theme.bgColor & 0xff;

  // Flat interior: slightly lighter than background
  const fillR = Math.min(bgR + 15, 255);
  const fillG = Math.min(bgG + 15, 255);
  const fillB = Math.min(bgB + 15, 255);

  const sandEdgeR = (theme.sandEdgeColor >> 16) & 0xff;
  const sandEdgeG = (theme.sandEdgeColor >> 8) & 0xff;
  const sandEdgeB = theme.sandEdgeColor & 0xff;
  const wireEdgeR = (theme.wireEdgeColor >> 16) & 0xff;
  const wireEdgeG = (theme.wireEdgeColor >> 8) & 0xff;
  const wireEdgeB = theme.wireEdgeColor & 0xff;

  // Pass 1: classify each thumbnail pixel
  // 0 = empty, 1 = solid interior, 2 = sand edge, 3 = wire edge
  const classify = new Uint8Array(THUMB_W * THUMB_H);

  for (let ty = 0; ty < THUMB_H; ty++) {
    for (let tx = 0; tx < THUMB_W; tx++) {
      const gx = Math.floor(tx * TERRAIN.GRID_WIDTH / THUMB_W);
      const gy = Math.floor(ty * gridHeight / THUMB_H);
      const cell = grid[gy * TERRAIN.GRID_WIDTH + gx];

      if (cell === 0) {
        classify[ty * THUMB_W + tx] = 0;
      } else {
        const isEdge = hasEmptyNeighbor(grid, gx, gy, TERRAIN.GRID_WIDTH, gridHeight);
        if (isEdge) {
          classify[ty * THUMB_W + tx] = cell === CELL.WIREFRAME ? 3 : 2;
        } else {
          classify[ty * THUMB_W + tx] = 1;
        }
      }
    }
  }

  // Pass 2: render pixels — edges bright, interior flat, empty with distance glow
  for (let ty = 0; ty < THUMB_H; ty++) {
    for (let tx = 0; tx < THUMB_W; tx++) {
      const ci = ty * THUMB_W + tx;
      const pi = ci * 4;
      const type = classify[ci];

      if (type === 1) {
        // Solid interior — flat dark fill
        pixels[pi]     = fillR;
        pixels[pi + 1] = fillG;
        pixels[pi + 2] = fillB;
        pixels[pi + 3] = 255;
      } else if (type >= 2) {
        // Edge — bright neon outline
        const isWire = type === 3;
        pixels[pi]     = isWire ? wireEdgeR : sandEdgeR;
        pixels[pi + 1] = isWire ? wireEdgeG : sandEdgeG;
        pixels[pi + 2] = isWire ? wireEdgeB : sandEdgeB;
        pixels[pi + 3] = 255;
      } else {
        // Empty — check for nearby edges to apply outer glow
        let minDist = Infinity;
        let glowR = sandEdgeR, glowG = sandEdgeG, glowB = sandEdgeB;

        for (let dy = -GLOW_RADIUS; dy <= GLOW_RADIUS; dy++) {
          const ny = ty + dy;
          if (ny < 0 || ny >= THUMB_H) continue;
          for (let dx = -GLOW_RADIUS; dx <= GLOW_RADIUS; dx++) {
            const nx = tx + dx;
            if (nx < 0 || nx >= THUMB_W) continue;
            const nt = classify[ny * THUMB_W + nx];
            if (nt >= 2) {
              const dist = Math.sqrt(dx * dx + dy * dy);
              if (dist < minDist) {
                minDist = dist;
                if (nt === 3) { glowR = wireEdgeR; glowG = wireEdgeG; glowB = wireEdgeB; }
                else          { glowR = sandEdgeR; glowG = sandEdgeG; glowB = sandEdgeB; }
              }
            }
          }
        }

        if (minDist <= GLOW_RADIUS) {
          const t = 1 - minDist / (GLOW_RADIUS + 1);
          const alpha = t * GLOW_STRENGTH;
          pixels[pi]     = Math.round(bgR + (glowR - bgR) * alpha);
          pixels[pi + 1] = Math.round(bgG + (glowG - bgG) * alpha);
          pixels[pi + 2] = Math.round(bgB + (glowB - bgB) * alpha);
        } else {
          pixels[pi]     = bgR;
          pixels[pi + 1] = bgG;
          pixels[pi + 2] = bgB;
        }
        pixels[pi + 3] = 255;
      }
    }
  }

  return pixels;
}
